/**
 * DCMS2 FormBuilder — programmatic creation of form elements
 *
 * JS library for dynamically creating DCMS2 form components identical
 * to those generated server-side by PHP/Smarty. HTML + head (styles/scripts) are fetched
 * from the PHP endpoint (/DCMS2/app/components/formBuilder.php), which guarantees full synchronization
 * with the TPL templates. Head resources are automatically deduplicated (styles, external scripts,
 * inline JS, CSS links) — creating the same component multiple times does not cause
 * the same resources to be loaded multiple times.
 *
 * Two equivalent approaches:
 *
 * 1) Options object (equivalent to Helper::create()):
 *    const el = await DCMSInput.create({ name: 'email', type: 'email', label: 'E-mail' });
 *    document.getElementById('target').appendChild(el);
 *
 * 2) Builder pattern (equivalent to a chain of helpers):
 *    const el = await new DCMSInput()
 *        .setName('email')
 *        .setType('email')
 *        .setLabel('E-mail')
 *        .render();
 *    document.getElementById('target').appendChild(el);
 *
 * Supported components: DCMSInput, DCMSTextarea, DCMSCheckbox, DCMSSelect, DCMSForm,
 *                         DCMSRadio, DCMSSwitchOnOff, DCMSDateTimePicker, DCMSColorPicker,
 *                         DCMSSlider, DCMSSortableList, DCMSDualList, DCMSTile,
 *                         DCMSTileGrid
 *
 * @see formBuilder.php — PHP endpoint (www/app/components/formBuilder.php)
 */

const FORM_BUILDER_URL = '/DCMS2/app/components/formBuilder.php';

// ──────────────────────────────────────────────────────────────────────────────
// DCMSFormElement — base class (equivalent to AbstractFormElement)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSFormElement {

    constructor() {
        // AbstractElement properties
        this.id = null;
        this.class = null;
        this.style = null;
        this.dataAttr = null;   // object { key: value }
        this.tpl = null;

        // AbstractFormElement properties
        this.name = null;
        this.value = null;
        this.label = null;
        this.required = null;
        this.readonly = null;
        this.disabled = null;
        this.autofocus = null;
        this.hidden = null;
        this.helpText = null;
        this.helpIcon = null;
        this.helpMode = null;
        this.validationState = null;
        this.validationMessage = null;
        this.ariaAttr = null;   // object { key: value }
        this.tabindex = null;
        this.accesskey = null;
        this.lang = null;
        this.dir = null;
        this.translate = null;
        this.draggable = null;
        this.contenteditable = null;
        this.spellcheck = null;
        this.inputmode = null;
        this.enterkeyhint = null;

        // Event handlers
        this.onBlur = null;
        this.onChange = null;
        this.onContextMenu = null;
        this.onFocus = null;
        this.onInput = null;
        this.onInvalid = null;
        this.onReset = null;
        this.onSearch = null;
        this.onSelect = null;
        this.onSubmit = null;
        this.onKeyDown = null;
        this.onKeyPress = null;
        this.onKeyUp = null;
        this.onClick = null;
        this.onDblClick = null;
        this.onMouseDown = null;
        this.onMouseMove = null;
        this.onMouseOut = null;
        this.onMouseOver = null;
        this.onMouseUp = null;
        this.onWheel = null;
        this.onDrag = null;
        this.onDragEnd = null;
        this.onDragEnter = null;
        this.onDragLeave = null;
        this.onDragOver = null;
        this.onDragStart = null;
        this.onDrop = null;
        this.onScroll = null;
        this.onCopy = null;
        this.onCut = null;
        this.onPaste = null;
    }

    // --- Builder methods (return this) ---

    setId(v)    { this.id = v; return this; }
    setClass(v) { this.class = v; return this; }
    setStyle(v) { this.style = v; return this; }
    setTpl(v)   { this.tpl = v; return this; }

    setName(v)     { this.name = v; return this; }
    setValue(v)     { this.value = v; return this; }
    setLabel(v)    { this.label = v; return this; }
    setRequired(v) { this.required = v; return this; }
    setReadonly(v) { this.readonly = v; return this; }
    setDisabled(v) { this.disabled = v; return this; }
    setAutofocus(v) { this.autofocus = v; return this; }
    setHidden(v)   { this.hidden = v; return this; }
    setTabindex(v) { this.tabindex = v; return this; }
    setAccesskey(v) { this.accesskey = v; return this; }
    setLang(v)     { this.lang = v; return this; }
    setDir(v)      { this.dir = v; return this; }
    /** @param {'yes'|'no'|boolean} v translate="yes" / "no" (true = 'yes', false = 'no'); other values = no attribute */
    setTranslate(v) { this.translate = v; return this; }
    setDraggable(v) { this.draggable = v; return this; }
    setContenteditable(v) { this.contenteditable = v; return this; }
    /** @param {boolean} [v=true] spellcheck="true" / "false" (not set = browser default) */
    setSpellcheck(v = true) { this.spellcheck = v; return this; }
    setInputmode(v) { this.inputmode = v; return this; }
    setEnterkeyhint(v) { this.enterkeyhint = v; return this; }

    /**
     * @param {string} text   Help text
     * @param {string} [icon] Material Symbols icon (default 'help')
     * @param {string} [mode] Display mode: 'hover' or 'click'
     */
    setHelpText(text, icon = 'help', mode = 'hover') {
        this.helpText = text;
        this.helpIcon = icon;
        this.helpMode = mode;
        return this;
    }

    /**
     * @param {number} state   0 = None, 1 = Success, 2 = Error
     * @param {string} message Validation message
     */
    setValidation(state, message = '') {
        this.validationState = state;
        this.validationMessage = message;
        return this;
    }

    /** @param {Object} obj e.g. { custom: 'value' } → data-custom="value" */
    setDataAttr(obj) { this.dataAttr = obj; return this; }

    /** @param {Object} obj e.g. { label: 'Opis' } → aria-label="Opis" */
    setAriaAttr(obj) { this.ariaAttr = obj; return this; }

    // Event handlers
    setOnBlur(v)     { this.onBlur = v; return this; }
    setOnChange(v)   { this.onChange = v; return this; }
    setOnContextMenu(v) { this.onContextMenu = v; return this; }
    setOnFocus(v)    { this.onFocus = v; return this; }
    setOnInput(v)    { this.onInput = v; return this; }
    setOnInvalid(v)  { this.onInvalid = v; return this; }
    setOnReset(v)    { this.onReset = v; return this; }
    setOnSearch(v)   { this.onSearch = v; return this; }
    setOnSelect(v)   { this.onSelect = v; return this; }
    setOnSubmit(v)   { this.onSubmit = v; return this; }
    setOnKeyDown(v)  { this.onKeyDown = v; return this; }
    setOnKeyPress(v) { this.onKeyPress = v; return this; }
    setOnKeyUp(v)    { this.onKeyUp = v; return this; }
    setOnClick(v)    { this.onClick = v; return this; }
    setOnDblClick(v) { this.onDblClick = v; return this; }
    setOnMouseDown(v) { this.onMouseDown = v; return this; }
    setOnMouseMove(v) { this.onMouseMove = v; return this; }
    setOnMouseOut(v)  { this.onMouseOut = v; return this; }
    setOnMouseOver(v) { this.onMouseOver = v; return this; }
    setOnMouseUp(v)   { this.onMouseUp = v; return this; }
    setOnWheel(v)     { this.onWheel = v; return this; }
    setOnDrag(v)      { this.onDrag = v; return this; }
    setOnDragEnd(v)   { this.onDragEnd = v; return this; }
    setOnDragEnter(v) { this.onDragEnter = v; return this; }
    setOnDragLeave(v) { this.onDragLeave = v; return this; }
    setOnDragOver(v)  { this.onDragOver = v; return this; }
    setOnDragStart(v) { this.onDragStart = v; return this; }
    setOnDrop(v)      { this.onDrop = v; return this; }
    setOnScroll(v)    { this.onScroll = v; return this; }
    setOnCopy(v)      { this.onCopy = v; return this; }
    setOnCut(v)       { this.onCut = v; return this; }
    setOnPaste(v)     { this.onPaste = v; return this; }

    /**
     * Collects the set properties into a params object (skips null/undefined)
     * @returns {Object}
     */
    _getParams() {
        const params = {};
        for (const [key, value] of Object.entries(this)) {
            if (value !== null && value !== undefined) {
                params[key] = value;
            }
        }
        return params;
    }

    /**
     * Sends a request to the PHP endpoint and returns the rendered HTMLElement
     * @param {string} componentName Component name (e.g. 'Input')
     * @returns {Promise<HTMLElement>}
     */
    async _fetchRender(componentName) {
        const response = await fetch(FORM_BUILDER_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                component: componentName,
                params: this._getParams()
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(`FormBuilder error: ${errorData.error || response.statusText}`);
        }

        const { html, head } = await response.json();

        // Inject head content (styles, scripts) — with deduplication.
        // DOMContentLoaded listeners of the head scripts wait for the insertion (see _onInserted).
        const headDomReadyListeners = [];
        if (head) {
            DCMSFormElement._injectHead(head, headDomReadyListeners);
        }

        const wrapper = document.createElement('div');
        wrapper.innerHTML = html.trim();

        // When the template generates multiple root elements (e.g. Tabs: nav + tab-content + script),
        // return the wrapper as a container; for single-root — return the element itself.
        let el;
        if (wrapper.children.length <= 1) {
            el = wrapper.firstElementChild || wrapper;
        } else {
            el = wrapper;
        }

        // After insertion into the DOM: validation, iframe resize, running inline scripts
        const validationState = this.validationState;
        const validationMessage = this.validationMessage || '';
        const id = this.id;
        const needsValidation = validationState != null && window.setValidationState;

        let _inserted = false;
        function _onInserted() {
            if (_inserted) return;
            _inserted = true;
            // Execute inline <script> tags from the rendered HTML (innerHTML does not run them).
            // We do this AFTER insertion into the DOM, so the scripts have access to the elements.
            // Attributes are copied: a data block (type="application/json") stays data and keeps its id.
            const bodyDomReadyListeners = [];
            DCMSFormElement._collectDomReadyListeners(() => {
                el.querySelectorAll('script').forEach(oldScript => {
                    const newScript = document.createElement('script');
                    for (const attr of Array.from(oldScript.attributes)) {
                        newScript.setAttribute(attr.name, attr.value);
                    }
                    if (!oldScript.src) {
                        newScript.textContent = oldScript.textContent;
                    }
                    oldScript.replaceWith(newScript);
                });
            }, bodyDomReadyListeners);
            // Template init wrapped in DOMContentLoaded — head first, as on a server-rendered page
            DCMSFormElement._runDomReadyListeners(headDomReadyListeners.concat(bodyDomReadyListeners));
            if (needsValidation) {
                setValidationState(id, validationState, validationMessage);
            }
            if (window.parentIFrame) {
                window.parentIFrame.size();
            }
        }

        const observer = new MutationObserver(() => {
            if (document.contains(el)) {
                observer.disconnect();
                _onInserted();
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        // Fallback timeout
        setTimeout(() => {
            observer.disconnect();
            if (document.contains(el)) {
                _onInserted();
            }
        }, 200);

        return el;
    }

    /**
     * Runs `execute` (code that runs component scripts) and collects the DOMContentLoaded listeners
     * those scripts add to `document`.
     *
     * Templates wrap their init in `document.addEventListener('DOMContentLoaded', …)`. On a server-rendered
     * page the event runs it; formBuilder inserts the component after the page has loaded, the event does not
     * come again and the init would never run. The collected listeners are run by _runDomReadyListeners()
     * once the component is in the DOM. The event itself is not dispatched again — page-level listeners
     * do not run twice. While the document is still loading nothing is collected: the listeners are added
     * to `document` and the real event runs them.
     *
     * Only scripts that run synchronously inside `execute` are covered (inline scripts; not `src`).
     *
     * @param {Function} execute
     * @param {Array|null} listeners Collected listeners are appended here; null — just run `execute`
     */
    static _collectDomReadyListeners(execute, listeners) {
        if (!listeners || document.readyState === 'loading') {
            execute();
            return;
        }
        const hadOwn = Object.prototype.hasOwnProperty.call(document, 'addEventListener');
        const addEventListener = document.addEventListener;
        document.addEventListener = function (type, listener, options) {
            if (type === 'DOMContentLoaded' && this === document) {
                // Same listener added twice runs once — as with addEventListener
                if (listener && !listeners.includes(listener)) {
                    listeners.push(listener);
                }
                return;
            }
            return addEventListener.call(this, type, listener, options);
        };
        try {
            execute();
        } finally {
            if (hadOwn) {
                document.addEventListener = addEventListener;
            } else {
                delete document.addEventListener;
            }
        }
    }

    /**
     * Runs DOMContentLoaded listeners collected by _collectDomReadyListeners(), in the order they were added.
     * An exception in one listener is reported (like in an event listener) and the next ones still run.
     *
     * @param {Array<Function|EventListenerObject>} listeners
     */
    static _runDomReadyListeners(listeners) {
        if (!listeners.length) return;
        const event = new Event('DOMContentLoaded');
        for (const listener of listeners) {
            try {
                if (typeof listener === 'function') {
                    listener.call(document, event);
                } else if (listener && typeof listener.handleEvent === 'function') {
                    listener.handleEvent(event);
                }
            } catch (error) {
                // Templates call setValidationState() from main.js in their init. formBuilder also works on
                // pages without main.js (it calls setValidationState only when present, see _fetchRender);
                // there the missing function is not reported — the init used to be skipped silently.
                if (error instanceof ReferenceError && typeof window.setValidationState !== 'function'
                    && String(error.message).includes('setValidationState')) {
                    continue;
                }
                if (typeof window.reportError === 'function') {
                    window.reportError(error);
                } else {
                    console.error(error);
                }
            }
        }
    }

    /**
     * Cache of injected head resources — prevents loading the same resources multiple times
     * @type {Set<string>}
     */
    static _injectedHeadHashes = new Set();

    /**
     * Parses and injects head content (styles, scripts) into the document with deduplication.
     *
     * Handles:
     *  - <style> — injected into <head>, deduplicated by content
     *  - <script src="..."> — external scripts, deduplicated by URL
     *  - <script>...</script> — inline JS, deduplicated by content
     *  - <link> — CSS, deduplicated by href
     *
     * @param {string} headHtml HTML fragment from the component head
     * @param {Array|null} [domReadyListeners] When given, DOMContentLoaded listeners added by the inline
     *                     scripts are collected here instead (see _collectDomReadyListeners)
     */
    static _injectHead(headHtml, domReadyListeners = null) {
        const parser = new DOMParser();
        const doc = parser.parseFromString('<head>' + headHtml + '</head>', 'text/html');
        const headElements = doc.head.children;

        for (const node of Array.from(headElements)) {
            const tagName = node.tagName.toLowerCase();

            if (tagName === 'style') {
                // Deduplication by CSS content
                const content = node.textContent.trim();
                if (!content) continue;
                const hash = 'style:' + DCMSFormElement._simpleHash(content);
                if (DCMSFormElement._injectedHeadHashes.has(hash)) continue;
                DCMSFormElement._injectedHeadHashes.add(hash);

                const style = document.createElement('style');
                style.textContent = content;
                style.setAttribute('data-dcms-formbuilder', '1');
                document.head.appendChild(style);

            } else if (tagName === 'script') {
                const src = node.getAttribute('src');
                if (src) {
                    // External script — deduplication by URL
                    const hash = 'script-src:' + src;
                    if (DCMSFormElement._injectedHeadHashes.has(hash)) continue;
                    // Check whether the script is not already loaded on the page
                    if (document.querySelector(`script[src="${CSS.escape(src)}"]`)) {
                        DCMSFormElement._injectedHeadHashes.add(hash);
                        continue;
                    }
                    DCMSFormElement._injectedHeadHashes.add(hash);

                    const script = document.createElement('script');
                    script.src = src;
                    if (node.getAttribute('type')) script.type = node.getAttribute('type');
                    script.setAttribute('data-dcms-formbuilder', '1');
                    document.head.appendChild(script);
                } else {
                    // Inline script — deduplication by content
                    const content = node.textContent.trim();
                    if (!content) continue;
                    const hash = 'script-inline:' + DCMSFormElement._simpleHash(content);
                    if (DCMSFormElement._injectedHeadHashes.has(hash)) continue;
                    DCMSFormElement._injectedHeadHashes.add(hash);

                    const script = document.createElement('script');
                    if (node.getAttribute('type')) script.type = node.getAttribute('type');
                    script.textContent = content;
                    script.setAttribute('data-dcms-formbuilder', '1');
                    DCMSFormElement._collectDomReadyListeners(() => {
                        document.head.appendChild(script);
                    }, domReadyListeners);
                }

            } else if (tagName === 'link') {
                const href = node.getAttribute('href');
                if (!href) continue;
                const hash = 'link:' + href;
                if (DCMSFormElement._injectedHeadHashes.has(hash)) continue;
                // Check whether the link does not already exist in the document
                if (document.querySelector(`link[href="${CSS.escape(href)}"]`)) {
                    DCMSFormElement._injectedHeadHashes.add(hash);
                    continue;
                }
                DCMSFormElement._injectedHeadHashes.add(hash);

                const link = document.createElement('link');
                for (const attr of node.attributes) {
                    link.setAttribute(attr.name, attr.value);
                }
                link.setAttribute('data-dcms-formbuilder', '1');
                document.head.appendChild(link);
            }
        }
    }

    /**
     * Simple string hash for deduplication (djb2)
     * @param {string} str
     * @returns {string}
     */
    static _simpleHash(str) {
        let hash = 5381;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
        }
        return hash.toString(36);
    }

    /**
     * Static helper — creates an instance from an options object and renders it
     * @param {Function} Cls  Component class (e.g. DCMSInput)
     * @param {string} component PHP component name (e.g. 'Input')
     * @param {Object} options Options object
     * @returns {Promise<HTMLElement>}
     */
    static async _createFromOptions(Cls, component, options = {}) {
        const inst = new Cls();
        for (const [key, value] of Object.entries(options)) {
            if (value === null || value === undefined) continue;
            inst[key] = value;
        }
        return inst._fetchRender(component);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSInput — Input component (equivalent to InputHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSInput extends DCMSFormElement {

    constructor() {
        super();
        this.type = null;
        this.placeholder = null;
        this.autocomplete = null;
        this.min = null;
        this.max = null;
        this.step = null;
        this.minlength = null;
        this.maxlength = null;

        // Char counter
        this.charCountMax = null;
        this.charCountWarning = null;
        this.charCountStrict = null;
        this.charCountWarningColor = null;
        this.charCountErrorColor = null;
        this.charCountErrorMessage = null;

        // File field (type 'file')
        this.multiple = null;
        this.accept = null;
    }

    setType(v)         { this.type = v; return this; }
    setPlaceholder(v)  { this.placeholder = v; return this; }
    setAutocomplete(v) { this.autocomplete = v; return this; }

    /** Type 'file': several files (name gets the [] suffix), a new selection is added to the list. */
    setMultiple(v = true) { this.multiple = v; return this; }

    /** Type 'file': accepted file types, e.g. 'image/png,image/svg+xml' or '.csv'. */
    setAccept(v)       { this.accept = v; return this; }

    setMinMaxStep(min, max, step) {
        this.min = min;
        this.max = max;
        this.step = step;
        return this;
    }

    setMinMaxLength(min, max) {
        this.minlength = min;
        this.maxlength = max;
        return this;
    }

    /**
     * @param {Object} opts
     * @param {number} opts.max           Maximum number of characters
     * @param {number} [opts.warning]     Warning threshold
     * @param {boolean} [opts.strict]     Block typing after exceeding max
     * @param {string} [opts.warningColor] Warning background color
     * @param {string} [opts.errorColor]   Error background color
     * @param {string} [opts.errorMessage] Error message
     */
    setCharCounter(opts) {
        if (opts.max !== undefined) this.charCountMax = opts.max;
        if (opts.warning !== undefined) this.charCountWarning = opts.warning;
        if (opts.strict !== undefined) this.charCountStrict = opts.strict;
        if (opts.warningColor !== undefined) this.charCountWarningColor = opts.warningColor;
        if (opts.errorColor !== undefined) this.charCountErrorColor = opts.errorColor;
        if (opts.errorMessage !== undefined) this.charCountErrorMessage = opts.errorMessage;
        return this;
    }

    async render() { return this._fetchRender('Input'); }

    /**
     * @param {Object} options Options object — keys identical to InputHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSInput, 'Input', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSTextarea — Textarea component (equivalent to TextareaHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSTextarea extends DCMSFormElement {

    constructor() {
        super();
        this.placeholder = null;
        this.rows = null;
        this.cols = null;
        this.wrap = null;           // 'soft' | 'hard'
        this.editor = null;         // 'ace' | 'tinymce' | 'editorjs' | null
        this.language = null;       // ACE: language (e.g. 'php', 'javascript')
        this.aceTheme = null;       // ACE: theme (e.g. 'tomorrow_night')
        this.tinyMceDefaultActive = null;
        this.tinyMceCustomOptions = null;
        this.editorJsTools = null;
        this.editorJsOptions = null;
        this.editorJsStorage = null;   // Editor.js: 'json' (default) | 'html' — value is HTML, converted on open/save
        this.editorJsStrict = null;    // Editor.js HTML storage: keep formatting outside the contract as raw HTML
        this.editorJsEmbeds = null;    // Editor.js HTML storage: dcms_* plugin embeds — {plugins, mediaManagerClass, ...} (TinyMCE option names)

        // Char counter
        this.charCountMax = null;
        this.charCountWarning = null;
        this.charCountStrict = null;
        this.charCountWarningColor = null;
        this.charCountErrorColor = null;
        this.charCountErrorMessage = null;
    }

    setPlaceholder(v)  { this.placeholder = v; return this; }
    setRows(v)         { this.rows = v; return this; }
    setCols(v)         { this.cols = v; return this; }
    /** @param {string} v 'soft' | 'hard' */
    setWrap(v)         { this.wrap = v; return this; }

    /** @param {string} editor 'ace' | 'tinymce' | 'editorjs' */
    setEditor(editor, opts = {}) {
        this.editor = editor;
        if (opts.language !== undefined) this.language = opts.language;
        if (opts.aceTheme !== undefined) this.aceTheme = opts.aceTheme;
        if (opts.tinyMceDefaultActive !== undefined) this.tinyMceDefaultActive = opts.tinyMceDefaultActive;
        if (opts.tinyMceCustomOptions !== undefined) this.tinyMceCustomOptions = opts.tinyMceCustomOptions;
        if (opts.editorJsTools !== undefined) this.editorJsTools = opts.editorJsTools;
        if (opts.editorJsOptions !== undefined) this.editorJsOptions = opts.editorJsOptions;
        if (opts.editorJsStorage !== undefined) this.editorJsStorage = opts.editorJsStorage;
        if (opts.editorJsStrict !== undefined) this.editorJsStrict = opts.editorJsStrict;
        if (opts.editorJsEmbeds !== undefined) this.editorJsEmbeds = opts.editorJsEmbeds;
        return this;
    }

    setCharCounter(opts) {
        if (opts.max !== undefined) this.charCountMax = opts.max;
        if (opts.warning !== undefined) this.charCountWarning = opts.warning;
        if (opts.strict !== undefined) this.charCountStrict = opts.strict;
        if (opts.warningColor !== undefined) this.charCountWarningColor = opts.warningColor;
        if (opts.errorColor !== undefined) this.charCountErrorColor = opts.errorColor;
        if (opts.errorMessage !== undefined) this.charCountErrorMessage = opts.errorMessage;
        return this;
    }

    async render() { return this._fetchRender('Textarea'); }

    /**
     * @param {Object} options Options object — keys identical to TextareaHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSTextarea, 'Textarea', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSCheckbox — Checkbox component (equivalent to CheckboxHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSCheckbox extends DCMSFormElement {

    constructor() {
        super();
        this.options = null;        // [{value: '1', label: 'Opcja 1'}, ...]
        this.values = null;         // ['1', '3'] — selected values
        this.minchecked = null;
        this.mincheckedMessage = null;
        this.maxchecked = null;
        this.maxcheckedMessage = null;
        this.requiredMessage = null;
        this.selectDeselectButton = null;
        this.columns = null;
        this.responsiveColumnsClass = null;
        this.filterCheckbox = null;
        this.minCheckboxesToEnableFilter = null;
        this.hideList = null;
        this.pills = null;          // render options as rounded toggle pills
        this.segmented = null;      // render options as a segmented control
    }

    /**
     * @param {Array<{value: string, label: string}>} opts List of options
     */
    setOptions(opts) { this.options = opts; return this; }

    /** @param {Array<string>} vals Selected values */
    setValues(vals) { this.values = vals; return this; }

    setMinchecked(min, message) {
        this.minchecked = min;
        if (message !== undefined) this.mincheckedMessage = message;
        return this;
    }
    setMaxchecked(max, message) {
        this.maxchecked = max;
        if (message !== undefined) this.maxcheckedMessage = message;
        return this;
    }

    setSelectDeselectButton(v = true) { this.selectDeselectButton = v; return this; }
    setColumns(cols, responsiveClass) {
        this.columns = cols;
        if (responsiveClass !== undefined) this.responsiveColumnsClass = responsiveClass;
        return this;
    }
    setFilterCheckbox(v = true, minItems) {
        this.filterCheckbox = v;
        if (minItems !== undefined) this.minCheckboxesToEnableFilter = minItems;
        return this;
    }

    /** Renders options as rounded toggle pills (see Checkbox::helperSetPills) */
    setPills(v = true) { this.pills = v; return this; }

    /** Renders options as a segmented control (see Checkbox::helperSetSegmented) */
    setSegmented(v = true) { this.segmented = v; return this; }

    async render() { return this._fetchRender('Checkbox'); }

    /**
     * @param {Object} options Options object — keys identical to CheckboxHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSCheckbox, 'Checkbox', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSSelect — Select component (equivalent to SelectHelper)
// Supports both the native <select> and Select2 (select2: true)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSSelect extends DCMSFormElement {

    constructor() {
        super();
        this.options = null;        // [{value: '1', label: 'Opcja 1'}, ...]
        this.values = null;         // Array — selected values (multiple)
        this.nullRow = null;        // Text of the empty option (e.g. '-- Wybierz --')
        this.multiple = null;

        // Select2
        this.select2 = null;        // true = use the Select2 library
        this.ajaxURL = null;         // AJAX URL (Select2)
        this.select2Options_allowClear = null;
        this.select2Options_maximumInputLength = null;
        this.select2Options_minimumInputLength = null;
        this.select2Options_maximumSelectionLength = null;
        this.select2Options_minimumResultsForSearch = null;

        // AJAX parent list
        this.ajaxParentList = null;
        this.ajaxLink = null;

        // Action links
        this.insertLink = null;
        this.insertLabel = null;
        this.viewLink = null;
        this.viewLabel = null;
        this.editLink = null;
        this.editLabel = null;

        // Validation
        this.minchecked = null;
        this.mincheckedMessage = null;
        this.maxchecked = null;
        this.maxcheckedMessage = null;
        this.requiredMessage = null;

        // Compact mode
        this.compactMode = null;
        this.compactTitle = null;
        this.compactDisplayLimit = null;
    }

    /**
     * @param {Array<{value: string, label: string}>} opts Lista opcji
     */
    setOptions(opts)   { this.options = opts; return this; }
    setValues(vals)    { this.values = vals; return this; }
    setNullRow(v)      { this.nullRow = v; return this; }
    setMultiple(v = true) { this.multiple = v; return this; }

    /** Enable Select2 with an optional AJAX URL */
    setSelect2(enabled = true, ajaxURL = null) {
        this.select2 = enabled;
        if (ajaxURL !== null) this.ajaxURL = ajaxURL;
        return this;
    }

    setSelect2Options(opts) {
        if (opts.allowClear !== undefined) this.select2Options_allowClear = opts.allowClear;
        if (opts.maximumInputLength !== undefined) this.select2Options_maximumInputLength = opts.maximumInputLength;
        if (opts.minimumInputLength !== undefined) this.select2Options_minimumInputLength = opts.minimumInputLength;
        if (opts.maximumSelectionLength !== undefined) this.select2Options_maximumSelectionLength = opts.maximumSelectionLength;
        if (opts.minimumResultsForSearch !== undefined) this.select2Options_minimumResultsForSearch = opts.minimumResultsForSearch;
        return this;
    }

    setAjaxParentList(parentId, ajaxLink) {
        this.ajaxParentList = parentId;
        this.ajaxLink = ajaxLink;
        return this;
    }

    setCompactMode(enabled = true, title = null, displayLimit = null) {
        this.compactMode = enabled;
        if (title !== null) this.compactTitle = title;
        if (displayLimit !== null) this.compactDisplayLimit = displayLimit;
        return this;
    }

    async render() { return this._fetchRender('Select'); }

    /**
     * @param {Object} options Options object — keys identical to SelectHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSSelect, 'Select', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSForm — Form component (equivalent to FormHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSForm extends DCMSFormElement {

    constructor() {
        super();
        this.acceptCharset = null;
        this.action = null;
        this.autocomplete = null;
        this.enctype = null;
        this.method = null;
        this.novalidate = null;
        this.rel = null;
        this.target = null;
        this.title = null;
        this.legendText = null;
        this.formBody = null;
        this.containerDiv = null;
        this.isSubmittedVarname = null;
        this.showFilterPills = null;

        // Submit button
        this.submitButtonText = null;
        this.submitButtonClass = null;
        this.submitButtonDirection = null;
        this.submitButtonId = null;

        // Reset button
        this.resetButtonText = null;
        this.resetButtonClass = null;
        this.resetButtonDirection = null;
    }

    setAction(v)        { this.action = v; return this; }
    setMethod(v)        { this.method = v; return this; }
    setEnctype(v)       { this.enctype = v; return this; }
    setAutocomplete(v)  { this.autocomplete = v; return this; }
    setNovalidate(v = true) { this.novalidate = v; return this; }
    setTarget(v)        { this.target = v; return this; }
    setTitle(v)         { this.title = v; return this; }
    setLegendText(v)    { this.legendText = v; return this; }
    setFormBody(v)      { this.formBody = v; return this; }

    setSubmitButton(text, btnClass, direction, id) {
        this.submitButtonText = text;
        if (btnClass !== undefined) this.submitButtonClass = btnClass;
        if (direction !== undefined) this.submitButtonDirection = direction;
        if (id !== undefined) this.submitButtonId = id;
        return this;
    }

    setResetButton(text, btnClass, direction) {
        this.resetButtonText = text;
        if (btnClass !== undefined) this.resetButtonClass = btnClass;
        if (direction !== undefined) this.resetButtonDirection = direction;
        return this;
    }

    async render() { return this._fetchRender('Form'); }

    /**
     * @param {Object} options Options object — keys identical to FormHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSForm, 'Form', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSRadio — Radio component (equivalent to RadioHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSRadio extends DCMSFormElement {

    constructor() {
        super();
        this.options = null;        // [{value: '1', label: 'Opcja 1'}, ...]
        this.requiredMessage = null;
        this.pills = null;          // render options as rounded toggle pills
        this.segmented = null;      // render options as a segmented control
    }

    /**
     * @param {Array<{value: string, label: string}>} opts Lista opcji
     */
    setOptions(opts) { this.options = opts; return this; }

    setRequiredMessage(v) { this.requiredMessage = v; return this; }

    /** Renders options as rounded toggle pills (see Radio::helperSetPills) */
    setPills(v = true) { this.pills = v; return this; }

    /** Renders options as a segmented control (see Radio::helperSetSegmented) */
    setSegmented(v = true) { this.segmented = v; return this; }

    async render() { return this._fetchRender('Radio'); }

    /**
     * @param {Object} options Options object — keys identical to RadioHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSRadio, 'Radio', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSSwitchOnOff — Switch component (equivalent to SwitchOnOffHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSSwitchOnOff extends DCMSFormElement {

    constructor() {
        super();
        this.title = null;
        this.description = null;
        this.requiredMessage = null;
    }

    setTitle(v)           { this.title = v; return this; }
    setDescription(v)     { this.description = v; return this; }
    setRequiredMessage(v) { this.requiredMessage = v; return this; }

    async render() { return this._fetchRender('SwitchOnOff'); }

    /**
     * @param {Object} options Options object — keys identical to SwitchOnOffHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSSwitchOnOff, 'SwitchOnOff', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSDateTimePicker — DateTimePicker component (equivalent to DateTimePickerHelper)
// Dates are passed as ISO strings (YYYY-MM-DD or YYYY-MM-DD HH:MM:SS) —
// the PHP endpoint converts them to \DateTime objects
// ──────────────────────────────────────────────────────────────────────────────

class DCMSDateTimePicker extends DCMSFormElement {

    constructor() {
        super();
        this.dateValue = null;      // ISO string or array of ISO strings
        this.rangeValues = null;    // array of [start, end] ISO strings
        this.minDate = null;        // ISO string
        this.maxDate = null;        // ISO string
        this.showRange = null;
        this.showTime = null;
        this.showRangesLine = null;
        this.requiredMessage = null;
        this.compactMode = null;
        this.modalTitle = null;
    }

    /** @param {string|Array<string>} v ISO date or array of dates */
    setDateValue(v)       { this.dateValue = v; return this; }
    setRangeValues(v)     { this.rangeValues = v; return this; }
    /** @param {string} v ISO date (YYYY-MM-DD) */
    setMinDate(v)         { this.minDate = v; return this; }
    /** @param {string} v ISO date (YYYY-MM-DD) */
    setMaxDate(v)         { this.maxDate = v; return this; }
    setShowRange(v = true)      { this.showRange = v; return this; }
    setShowTime(v = true)       { this.showTime = v; return this; }
    setShowRangesLine(v = true) { this.showRangesLine = v; return this; }
    setRequiredMessage(v) { this.requiredMessage = v; return this; }
    setCompactMode(enabled = true, title = null) {
        this.compactMode = enabled;
        if (title !== null) this.modalTitle = title;
        return this;
    }

    async render() { return this._fetchRender('DateTimePicker'); }

    /**
     * @param {Object} options Options object — keys identical to DateTimePickerHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSDateTimePicker, 'DateTimePicker', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSColorPicker — ColorPicker component (equivalent to ColorPickerHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSColorPicker extends DCMSFormElement {

    constructor() {
        super();
        this.format = null;         // 'hex' | 'rgb' | 'hsl'
        this.useAlpha = null;
        this.orientation = null;    // 'horizontal' | 'vertical'
        this.showPreview = null;
        this.showInput = null;
        this.swatches = null;       // array of colors
        this.defaultColor = null;
        this.headTpl = null;
    }

    /** @param {string} v 'hex' | 'rgb' | 'hsl' */
    setFormat(v)       { this.format = v; return this; }
    setUseAlpha(v = true) { this.useAlpha = v; return this; }
    /** @param {string} v 'horizontal' | 'vertical' */
    setOrientation(v)  { this.orientation = v; return this; }
    setShowPreview(v = true) { this.showPreview = v; return this; }
    setShowInput(v = true)   { this.showInput = v; return this; }
    /** @param {Array<string>} colors Array of colors e.g. ['#ff0000', '#00ff00'] */
    setSwatches(colors) { this.swatches = colors; return this; }
    setDefaultColor(v) { this.defaultColor = v; return this; }
    setHeadTpl(v)      { this.headTpl = v; return this; }

    async render() { return this._fetchRender('ColorPicker'); }

    /**
     * @param {Object} options Options object — keys identical to ColorPickerHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSColorPicker, 'ColorPicker', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSSlider — Slider component (equivalent to SliderHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSSlider extends DCMSFormElement {

    constructor() {
        super();
        this.min = null;
        this.max = null;
        this.step = null;
        this.orientation = null;    // 'horizontal' | 'vertical'
        this.tooltip = null;        // 'show' | 'hide' | 'always'
        this.handle = null;         // 'round' | 'square'
        this.formatter = null;      // name of the JS function
        this.showInput = null;
        this.showMinMax = null;
        this.suffix = null;
        this.prefix = null;
        this.color = null;          // Tailwind color
        this.headTpl = null;
    }

    setMin(v)          { this.min = v; return this; }
    setMax(v)          { this.max = v; return this; }
    setStep(v)         { this.step = v; return this; }
    setMinMaxStep(min, max, step) {
        this.min = min;
        this.max = max;
        this.step = step;
        return this;
    }
    setOrientation(v)  { this.orientation = v; return this; }
    /** @param {string} v 'show' | 'hide' | 'always' */
    setTooltip(v)      { this.tooltip = v; return this; }
    /** @param {string} v 'round' | 'square' */
    setHandle(v)       { this.handle = v; return this; }
    setFormatter(v)    { this.formatter = v; return this; }
    setShowInput(v = true)  { this.showInput = v; return this; }
    setShowMinMax(v = true) { this.showMinMax = v; return this; }
    setSuffix(v)       { this.suffix = v; return this; }
    setPrefix(v)       { this.prefix = v; return this; }
    setColor(v)        { this.color = v; return this; }
    setHeadTpl(v)      { this.headTpl = v; return this; }

    async render() { return this._fetchRender('Slider'); }

    /**
     * @param {Object} options Options object — keys identical to SliderHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSSlider, 'Slider', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSSortableList — SortableList component (equivalent to SortableListHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSSortableList extends DCMSFormElement {

    constructor() {
        super();
        this.items = null;           // [{id: 1, label: 'Element', priority: 10}, ...]
        this.allowDelete = null;
        this.priorityStep = null;
        this.prioritySortOrder = null; // 'asc' | 'desc'
        this.compactMode = null;
        this.modalTitle = null;
        this.moveUpText = null;
        this.moveDownText = null;
        this.deleteText = null;
        this.deleteConfirmText = null;
    }

    /**
     * @param {Array<{id: number|string, label: string, priority: number}>} items List items
     */
    setItems(items)    { this.items = items; return this; }
    setAllowDelete(v = true)     { this.allowDelete = v; return this; }
    setPriorityStep(v)           { this.priorityStep = v; return this; }
    /** @param {string} v 'asc' | 'desc' */
    setPrioritySortOrder(v)      { this.prioritySortOrder = v; return this; }
    setCompactMode(enabled = true, title = null) {
        this.compactMode = enabled;
        if (title !== null) this.modalTitle = title;
        return this;
    }
    setTexts(opts) {
        if (opts.moveUp !== undefined) this.moveUpText = opts.moveUp;
        if (opts.moveDown !== undefined) this.moveDownText = opts.moveDown;
        if (opts.deleteText !== undefined) this.deleteText = opts.deleteText;
        if (opts.deleteConfirm !== undefined) this.deleteConfirmText = opts.deleteConfirm;
        return this;
    }

    async render() { return this._fetchRender('SortableList'); }

    /**
     * @param {Object} options Options object — keys identical to SortableListHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSSortableList, 'SortableList', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSTree — Tree component (equivalent to TreeHelper::create)
// Row cells are plain configs — {type: 'checkbox'|'radio'|'switch'|'text'|'dropdown'|'iconButton'|'badge',
// key, label, icon, ...} — the endpoint turns them into Tree::cell*() descriptors.
// ──────────────────────────────────────────────────────────────────────────────

class DCMSTree extends DCMSFormElement {

    constructor() {
        super();
        this.nodes = null;              // [{id, label, children, cells, expanded, disabled, hiddenCells}, ...]
        this.rowCells = null;           // [{type, key, label, ...}] — see the class comment
        this.dropPositions = null;      // ['before', 'after', 'inside']
        this.maxDepth = null;
        this.showLine = null;
        this.defaultExpandAll = null;
        this.selectable = null;
        this.searchable = null;
        this.searchDebounceMs = null;
        this.searchMinChars = null;
        this.searchRemoteUrl = null;
        this.searchKeepsActive = null;  // false — a search does not force checked nodes visible
        this.nodeFieldMap = null;
        this.compactMode = null;
        this.modalTitle = null;
        this.minSelection = null;
        this.checkboxSide = null;       // 'left' | 'right'
        this.filters = null;            // [{key, label, cell, active, icon}] — toolbar filters
        this.displayTemplate = null;    // compact field text: '{nodes}', '{cell:<key>}'
        this.columnHeaders = null;      // fixed-width columns + header row
        this.columnWidth = null;        // 'sm' | 'md' | 'lg'
        this.rowBulkToggle = null;      // ['fokus', 'panorama'] — row "+" / "−" buttons
        this.collapsedShowsChecked = null; // cell key — collapsed nodes keep showing checked children
        this.labelClick = null;         // 'toggle' | 'smart' — what a click on the node label does
        this.fillHeight = null;         // inline field fills its parent's height
        this.collapseAllDepth = null;   // depth kept expanded by "Collapse all" (0 = roots too)
        this.lineStyle = null;          // 'connectors' — file-explorer elbows + [+] / [−] boxes
        this.narrowWrap = null;         // false — rows keep one line on a narrow tree
    }

    /** @param {Array<Object>} nodes Node descriptors (same shape as Tree::$nodes) */
    setNodes(nodes) { this.nodes = nodes; return this; }

    /** @param {Array<Object>} cells Row cell configs ({type, key, label, ...}) */
    setRowCells(cells) { this.rowCells = cells; return this; }

    /** Drag & drop reordering (the inherited HTML `draggable` property — same key as TreeHelper::create) */
    setDraggable(v = true, positions) {
        this.draggable = v;
        if (positions !== undefined) this.dropPositions = positions;
        return this;
    }
    setMaxDepth(depth) { this.maxDepth = depth; return this; }
    setShowLine(v = true) { this.showLine = v; return this; }
    setDefaultExpandAll(v = true) { this.defaultExpandAll = v; return this; }
    setSelectable(v = true) { this.selectable = v; return this; }

    setSearchable(v = true, debounceMs, remoteUrl, minChars, keepActiveVisible) {
        this.searchable = v;
        if (debounceMs !== undefined) this.searchDebounceMs = debounceMs;
        if (remoteUrl !== undefined) this.searchRemoteUrl = remoteUrl;
        if (minChars !== undefined) this.searchMinChars = minChars;
        if (keepActiveVisible !== undefined) this.searchKeepsActive = keepActiveVisible;
        return this;
    }

    setNodeFieldMap(map) { this.nodeFieldMap = map; return this; }
    setCompactMode(v = true, modalTitle) {
        this.compactMode = v;
        if (modalTitle !== undefined) this.modalTitle = modalTitle;
        return this;
    }
    setMinSelection(min) { this.minSelection = min; return this; }
    setCheckboxSide(side) { this.checkboxSide = side; return this; }

    /** @param {Array<{key: string, label: string, cell: string, active?: boolean, icon?: string}>} filters */
    setFilters(filters) { this.filters = filters; return this; }
    setDisplayTemplate(template) { this.displayTemplate = template; return this; }
    setColumnHeaders(v = true, width) {
        this.columnHeaders = v;
        if (width !== undefined) this.columnWidth = width;
        return this;
    }

    /** @param {Array<string>} cellKeys Checkbox cells the row "+" / "−" buttons toggle */
    setRowBulkToggle(cellKeys) { this.rowBulkToggle = cellKeys; return this; }

    /** @param {string} cellKey Collapsed nodes keep showing the children with this cell checked */
    setCollapsedShowsChecked(cellKey) { this.collapsedShowsChecked = cellKey; return this; }

    /** @param {'toggle'|'smart'} mode What a click on the node label does */
    setLabelClick(mode) { this.labelClick = mode; return this; }

    /** Inline field fills its parent's height (no 70vh cap) */
    setFillHeight(v = true) { this.fillHeight = v; return this; }

    /** @param {number} depth Depth kept expanded by "Collapse all" (0 = collapse roots too) */
    setCollapseAllDepth(depth) { this.collapseAllDepth = depth; return this; }

    /** @param {'default'|'connectors'} style Hierarchy line style (turns lines on) */
    setLineStyle(style) { this.lineStyle = style; this.showLine = true; return this; }

    /** @param {boolean} wrap Cells wrap under the label on a narrow tree (default true) */
    setNarrowWrap(wrap = true) { this.narrowWrap = wrap; return this; }

    async render() { return this._fetchRender('Tree'); }

    /**
     * @param {Object} options Options object — keys identical to TreeHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSTree, 'Tree', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSDualList — DualList component (equivalent to DualListHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSDualList extends DCMSFormElement {

    constructor() {
        super();
        this.listA = null;           // [{id: 1, label: 'Element', priority: 10}, ...]
        this.listB = null;           // [{id: 2, label: 'Element 2', priority: 20}, ...]
        this.listALabel = null;
        this.listBLabel = null;
        this.sortable = null;
        this.priorityStep = null;
        this.prioritySortOrder = null; // 'asc' | 'desc'
        this.compactMode = null;
        this.modalTitle = null;
        this.moveToRightText = null;
        this.moveToLeftText = null;
        this.moveAllToRightText = null;
        this.moveAllToLeftText = null;
        this.moveUpText = null;
        this.moveDownText = null;
    }

    /**
     * @param {Array<{id: number|string, label: string, priority?: number}>} items List A items
     * @param {string} [label] List A label
     */
    setListA(items, label) {
        this.listA = items;
        if (label !== undefined) this.listALabel = label;
        return this;
    }
    /**
     * @param {Array<{id: number|string, label: string, priority?: number}>} items List B items
     * @param {string} [label] List B label
     */
    setListB(items, label) {
        this.listB = items;
        if (label !== undefined) this.listBLabel = label;
        return this;
    }
    setSortable(v = true)        { this.sortable = v; return this; }
    setPriorityStep(v)           { this.priorityStep = v; return this; }
    setPrioritySortOrder(v)      { this.prioritySortOrder = v; return this; }
    setCompactMode(enabled = true, title = null) {
        this.compactMode = enabled;
        if (title !== null) this.modalTitle = title;
        return this;
    }
    setTexts(opts) {
        if (opts.moveToRight !== undefined) this.moveToRightText = opts.moveToRight;
        if (opts.moveToLeft !== undefined) this.moveToLeftText = opts.moveToLeft;
        if (opts.moveAllToRight !== undefined) this.moveAllToRightText = opts.moveAllToRight;
        if (opts.moveAllToLeft !== undefined) this.moveAllToLeftText = opts.moveAllToLeft;
        if (opts.moveUp !== undefined) this.moveUpText = opts.moveUp;
        if (opts.moveDown !== undefined) this.moveDownText = opts.moveDown;
        return this;
    }

    async render() { return this._fetchRender('DualList'); }

    /**
     * @param {Object} options Options object — keys identical to DualListHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSDualList, 'DualList', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSButton — Button component (equivalent to ButtonHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSButton extends DCMSFormElement {

    constructor() {
        super();
        this.type = null;           // 'button' | 'submit' | 'reset'
        this.form = null;           // form ID
        this.formaction = null;
        this.formenctype = null;
        this.formmethod = null;
        this.formtarget = null;
        this.formnovalidate = null;
        this.content = null;        // HTML content inside the button
        this.onActions = null;
    }

    /** @param {string} v 'button' | 'submit' | 'reset' */
    setType(v)            { this.type = v; return this; }
    setForm(v)            { this.form = v; return this; }
    setFormaction(v)      { this.formaction = v; return this; }
    setFormenctype(v)     { this.formenctype = v; return this; }
    setFormmethod(v)      { this.formmethod = v; return this; }
    setFormtarget(v)      { this.formtarget = v; return this; }
    setFormnovalidate(v = true) { this.formnovalidate = v; return this; }
    setContent(v)         { this.content = v; return this; }
    setOnActions(v)       { this.onActions = v; return this; }

    async render() { return this._fetchRender('Button'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSButton, 'Button', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSImage64 — Image64 component (equivalent to Image64Helper)
// Image upload with preview, stored as base64
// ──────────────────────────────────────────────────────────────────────────────

class DCMSImage64 extends DCMSFormElement {

    constructor() {
        super();
        this.maxFileSize = null;    // max file size in KB
        this.extensions = null;     // allowed extensions ['jpg','png']
        this.width = null;          // preview width px
        this.height = null;         // preview height px
    }

    setMaxFileSize(v)  { this.maxFileSize = v; return this; }
    setExtensions(v)   { this.extensions = v; return this; }
    setWidth(v)        { this.width = v; return this; }
    setHeight(v)       { this.height = v; return this; }

    async render() { return this._fetchRender('Image64'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSImage64, 'Image64', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSDataTables — DataTables component (equivalent to DataTablesHelper)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSDataTables extends DCMSFormElement {

    constructor() {
        super();
        this.ajaxUrl = null;
        this.ajaxMethod = null;
        this.columns = null;
        this.data = null;
        this.serverSide = null;
        this.paging = null;
        this.ordering = null;
        this.searching = null;
        this.info = null;
        this.pageLength = null;
        this.lengthMenu = null;
        this.order = null;
        this.language = null;
        this.responsive = null;
        this.select = null;
        this.selectStyle = null;
        this.buttons = null;
        this.buttonsList = null;
        this.autoRefresh = null;
        this.autoRefreshInterval = null;
        this.stateSave = null;
        this.stateSaveKey = null;
        this.drawCallback = null;
        this.initComplete = null;
        this.ajaxDataCallback = null;
        this.customOptions = null;
    }

    setAjaxUrl(url, method = 'POST') { this.ajaxUrl = url; this.ajaxMethod = method; return this; }
    setColumns(v)          { this.columns = v; return this; }
    setData(v)             { this.data = v; return this; }
    setServerSide(v = true) { this.serverSide = v; return this; }
    setPaging(enabled = true, pageLength = 25, lengthMenu = null) {
        this.paging = enabled;
        this.pageLength = pageLength;
        if (lengthMenu) this.lengthMenu = lengthMenu;
        return this;
    }
    setOrdering(enabled = true, order = null) {
        this.ordering = enabled;
        if (order) this.order = order;
        return this;
    }
    setSearching(v = true) { this.searching = v; return this; }
    setLanguage(v)         { this.language = v; return this; }
    setResponsive(v = true) { this.responsive = v; return this; }
    setSelect(enabled = true, style = 'multi') { this.select = enabled; this.selectStyle = style; return this; }
    setButtons(enabled = true, list = null) { this.buttons = enabled; if (list) this.buttonsList = list; return this; }
    setAutoRefresh(enabled = true, interval = 30000) { this.autoRefresh = enabled; this.autoRefreshInterval = interval; return this; }
    setStateSave(enabled = true, key = null) { this.stateSave = enabled; if (key) this.stateSaveKey = key; return this; }

    async render() { return this._fetchRender('DataTables'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSDataTables, 'DataTables', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSTabs — Tabs component (tabs)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSTabs extends DCMSFormElement {

    constructor() {
        super();
        this.style = null;  // 'classic' | 'modern' (default)
        this.tabs = [];     // [{title, body, iconM?, active?, id?, url?}]
    }

    /**
     * Sets the tab style: 'classic' (border, teal) or 'modern' (border-bottom, indigo)
     * @param {string} v  'classic' or 'modern'
     */
    setStyle(v) { this.style = v; return this; }

    /**
     * @param {string} title  Tab title
     * @param {string} body   HTML content
     * @param {Object} [opts] Options: iconM, active, id, url
     */
    addTab(title, body, opts = {}) {
        this.tabs.push({
            title,
            body,
            iconM: opts.iconM || null,
            active: opts.active || false,
            id: opts.id || null,
            url: opts.url || null
        });
        return this;
    }

    async render() { return this._fetchRender('Tabs'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSTabs, 'Tabs', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSPanel — Panel component
// ──────────────────────────────────────────────────────────────────────────────

class DCMSPanel extends DCMSFormElement {

    constructor() {
        super();
        this.title = null;
        this.body = null;
        this.headClass = null;
        this.bodyClass = null;
        this.accordion = null;      // render as a native <details>/<summary> accordion
        this.accordionOpen = null;  // initial open/closed state (default: open)
        this.icon = null;           // Material Symbols icon before the title (feature header / accordion)
        this.badge = null;          // badge text next to the title
        this.variant = null;        // colour template: PanelVariant value ('default', 'soft', 'tint', 'white', 'blue-header', 'slate', 'classic')
        this.subtitle = null;       // muted line under the title
        this.headActions = null;    // HTML on the right side of the header
    }

    setTitle(v)      { this.title = v; return this; }
    setBody(v)       { this.body = v; return this; }
    setHeadClass(v)  { this.headClass = v; return this; }
    setBodyClass(v)  { this.bodyClass = v; return this; }

    /**
     * Sets the colour template (see Panel::helperSetVariant in PHP).
     * @param {string} v PanelVariant value, e.g. 'slate'
     */
    setVariant(v)    { this.variant = v; return this; }

    /**
     * Adds structured header content (see Panel::helperSetHeader in PHP).
     * With an icon the header switches to the feature layout.
     * @param {Object} [header]
     * @param {string} [header.icon] Material Symbols icon name
     * @param {string} [header.subtitle] Muted line under the title
     * @param {string} [header.badge] Badge text next to the title
     * @param {string} [header.actions] HTML on the right side of the header
     */
    setHeader({ icon, subtitle, badge, actions } = {}) {
        if (icon !== undefined) this.icon = icon;
        if (subtitle !== undefined) this.subtitle = subtitle;
        if (badge !== undefined) this.badge = badge;
        if (actions !== undefined) this.headActions = actions;
        return this;
    }

    /**
     * Renders the panel as an accordion (see Panel::helperSetAccordion in PHP).
     * @param {boolean} [open=true] Initial state (true = expanded)
     * @param {string} [icon] Material Symbols icon name shown before the title
     * @param {string} [badge] Badge text next to the title
     */
    setAccordion(open = true, icon, badge) {
        this.accordion = true;
        this.accordionOpen = open;
        if (icon !== undefined) this.icon = icon;
        if (badge !== undefined) this.badge = badge;
        return this;
    }

    async render() { return this._fetchRender('Panel'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSPanel, 'Panel', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSGrid — Grid component (responsive grid)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSGrid extends DCMSFormElement {

    constructor() {
        super();
        this.gap = null;
        this.size = null;
        this.sizeSm = null;
        this.sizeMd = null;
        this.sizeLg = null;
        this.sizeXl = null;
        this.size2xl = null;
        this.elements = [];     // [{html, size?, sizeSm?, ..., class?, id?}]
    }

    setSize(size, opts = {}) {
        this.size = size;
        if (opts.gap !== undefined) this.gap = opts.gap;
        if (opts.sm !== undefined) this.sizeSm = opts.sm;
        if (opts.md !== undefined) this.sizeMd = opts.md;
        if (opts.lg !== undefined) this.sizeLg = opts.lg;
        if (opts.xl !== undefined) this.sizeXl = opts.xl;
        if (opts['2xl'] !== undefined) this.size2xl = opts['2xl'];
        return this;
    }

    /**
     * @param {string} html  Element's HTML content
     * @param {Object} [opts] Options: size, sizeSm, sizeMd, sizeLg, sizeXl, size2xl, class, id
     */
    addElement(html, opts = {}) {
        this.elements.push({
            html,
            size: opts.size || 1,
            sizeSm: opts.sm || null,
            sizeMd: opts.md || null,
            sizeLg: opts.lg || null,
            sizeXl: opts.xl || null,
            size2xl: opts['2xl'] || null,
            class: opts.class || null,
            id: opts.id || null
        });
        return this;
    }

    async render() { return this._fetchRender('Grid'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSGrid, 'Grid', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSTable — Table component
// ──────────────────────────────────────────────────────────────────────────────

class DCMSTable extends DCMSFormElement {

    constructor() {
        super();
        this.caption = null;
        this.mainColor = null;
        this.columns = null;    // [{id, name, title, sortable?, width?, align?}]
        this.rows = null;       // [{items: [{value, align?, class?}], class?, id?}]
        this.condensed = null;
        this.fixedWidth = null;
        this.sortable = null;
        this.sortableCallback = null;
        this.sortInternal = null;
    }

    setCaption(v)     { this.caption = v; return this; }
    setMainColor(v)   { this.mainColor = v; return this; }
    setColumns(v)     { this.columns = v; return this; }
    setRows(v)        { this.rows = v; return this; }
    setCondensed(v = true)  { this.condensed = v; return this; }
    setFixedWidth(v = true) { this.fixedWidth = v; return this; }
    setSortable(callback = null, internal = false) {
        this.sortable = true;
        this.sortableCallback = callback;
        this.sortInternal = internal;
        return this;
    }

    async render() { return this._fetchRender('Table'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSTable, 'Table', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSAlert — Alert component
// ──────────────────────────────────────────────────────────────────────────────

class DCMSAlert extends DCMSFormElement {

    constructor() {
        super();
        this.message = null;
        this.title = null;
        this.informationStyle = null;   // 'Success' | 'Warning' | 'Error' | 'Neutral' | 'SystemError'
        this.showIcon = null;
        this.customIcon = null;
        this.maxWidth = null;
    }

    setMessage(v)     { this.message = v; return this; }
    setTitle(v)       { this.title = v; return this; }
    /** @param {string} v 'Success' | 'Warning' | 'Error' | 'Neutral' | 'SystemError' */
    setInformationStyle(v) { this.informationStyle = v; return this; }
    setShowIcon(v = true)  { this.showIcon = v; return this; }
    setCustomIcon(v)  { this.customIcon = v; return this; }
    setMaxWidth(v)    { this.maxWidth = v; return this; }

    async render() { return this._fetchRender('Alert'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSAlert, 'Alert', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSModal — Modal component
// ──────────────────────────────────────────────────────────────────────────────

class DCMSModal extends DCMSFormElement {

    constructor() {
        super();
        this.title = null;
        this.body = null;
        this.engine = null;         // 'dcms-modals' | 'iziModal'
        this.options = null;        // engine options
        this.headClass = null;
        this.bodyClass = null;
    }

    setTitle(v)       { this.title = v; return this; }
    setBody(v)        { this.body = v; return this; }
    setEngine(v)      { this.engine = v; return this; }
    setOptions(v)     { this.options = v; return this; }
    setHeadClass(v)   { this.headClass = v; return this; }
    setBodyClass(v)   { this.bodyClass = v; return this; }

    async render() { return this._fetchRender('Modal'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSModal, 'Modal', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSSkeleton — Skeleton component (loading placeholder)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSSkeleton extends DCMSFormElement {

    constructor() {
        super();
        this.type = null;            // 'paragraph' | 'title' | 'avatar' | 'image' | 'button' | 'card' | 'table-row' | 'custom'
        this.rows = null;
        this.animated = null;
        this.showAvatar = null;
        this.showTitle = null;
        this.width = null;
        this.height = null;
        this.columns = null;
    }

    setType(v)        { this.type = v; return this; }
    setRows(v)        { this.rows = v; return this; }
    setAnimated(v)    { this.animated = v; return this; }
    setShowAvatar(v)  { this.showAvatar = v; return this; }
    setShowTitle(v)   { this.showTitle = v; return this; }
    setWidth(v)       { this.width = v; return this; }
    setHeight(v)      { this.height = v; return this; }
    setColumns(v)     { this.columns = v; return this; }

    async render() { return this._fetchRender('Skeleton'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSSkeleton, 'Skeleton', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSTag — Tag component (chip / inline label)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSTag extends DCMSFormElement {

    constructor() {
        super();
        this.label = null;
        this.color = null;           // 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'pink' | 'orange'
        this.size = null;            // 'sm' | 'md' | 'lg'
        this.icon = null;
        this.closable = null;
        this.onCloseUrl = null;
        this.onCloseJs = null;
        this.clickable = null;
        this.href = null;
    }

    setLabel(v)        { this.label = v; return this; }
    setColor(v)        { this.color = v; return this; }
    setSize(v)         { this.size = v; return this; }
    setIcon(v)         { this.icon = v; return this; }
    setClosable(v, url = null, js = null) {
        this.closable = v;
        this.onCloseUrl = url;
        this.onCloseJs = js;
        return this;
    }
    setClickable(v, href = null) {
        this.clickable = v;
        this.href = href;
        return this;
    }

    async render() { return this._fetchRender('Tag'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSTag, 'Tag', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSTile — Tile component (dashboard entry tile)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSTile extends DCMSFormElement {

    constructor() {
        super();
        this.title = null;
        this.description = null;
        this.icon = null;            // Material Symbols Rounded icon
        this.badge = null;
        this.accent = null;          // 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'pink' | 'orange'
        this.href = null;
        this.target = null;
        this.featured = null;
        this.disabled = null;
        this.watermark = null;
        this.goIcon = null;
    }

    setTitle(v)       { this.title = v; return this; }
    setDescription(v) { this.description = v; return this; }
    setIcon(v)        { this.icon = v; return this; }
    setBadge(v)       { this.badge = v; return this; }
    setAccent(v)      { this.accent = v; return this; }
    setLink(href, target = null) {
        this.href = href;
        this.target = target;
        return this;
    }
    setFeatured(v)    { this.featured = v; return this; }
    setDisabled(v)    { this.disabled = v; return this; }
    setDecoration(watermark = true, goIcon = 'arrow_forward') {
        this.watermark = watermark;
        this.goIcon = goIcon;
        return this;
    }

    async render() { return this._fetchRender('Tile'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSTile, 'Tile', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSTileGrid — TileGrid component (bento grid for tiles)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSTileGrid extends DCMSFormElement {

    constructor() {
        super();
        this.columns = null;    // number of columns >= 1024px (2-6)
        this.tiles = [];        // [{html, featured?, class?, id?}]
    }

    setColumns(v) { this.columns = v; return this; }

    /**
     * Adds a grid cell.
     *
     * @param {string|HTMLElement} tile Tile — HTML or an element returned by DCMSTile
     * @param {Object} [opts] Options: featured, class, id
     */
    addTile(tile, opts = {}) {
        this.tiles.push({
            html: tile instanceof HTMLElement ? tile.outerHTML : tile,
            featured: opts.featured || false,
            class: opts.class || null,
            id: opts.id || null
        });
        return this;
    }

    async render() { return this._fetchRender('TileGrid'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSTileGrid, 'TileGrid', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSCalendar — Calendar component (month / week / day / agenda grid)
// Behaviour after insertion: www/js/dcms-calendar.js (auto-init).
// ──────────────────────────────────────────────────────────────────────────────

class DCMSCalendar extends DCMSFormElement {

    constructor() {
        super();
        this.initialView = null;     // 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek' | 'listDay' | 'listMonth' | 'multiMonthYear'
        this.views = null;           // views of the toolbar switch
        this.mobileView = null;      // view forced on narrow containers; false = keep the current view
        this.mobileBreakpoint = null;
        this.initialDate = null;     // 'Y-m-d'
        this.events = null;          // [{id, title, start, end, allDay, color, url, description, sourceId}]
        this.eventsUrl = null;       // JSON feed (GET start, end)
        this.eventsParams = null;
        this.sources = null;         // [{id, label, color, active}]
        this.sourceToggleUrl = null;
        this.addUrl = null;
        this.addLabel = null;
        this.addModalTitle = null;
        this.linkTarget = null;      // 'modal' | 'frame' | 'top' | 'blank'
        this.modalWidth = null;
        this.height = null;          // 'auto' | px
        this.timeGridHeight = null;
        this.slotMinTime = null;
        this.slotMaxTime = null;
        this.scrollTime = null;
        this.selectable = null;
        this.editable = null;
        this.nowIndicator = null;
        this.weekNumbers = null;
        this.dayMaxEvents = null;
        this.libUrl = null;
    }

    /**
     * @param {string} initialView
     * @param {string[]|null} [views]
     * @param {string|false} [mobileView] false = keep the current view on narrow screens
     */
    setView(initialView, views = null, mobileView = 'listWeek') {
        this.initialView = initialView;
        this.views = views;
        this.mobileView = mobileView;
        return this;
    }
    setMobileBreakpoint(v) { this.mobileBreakpoint = v; return this; }
    setInitialDate(v)      { this.initialDate = v; return this; }
    setEvents(v)           { this.events = v; return this; }
    addEvent(event) {
        if (!Array.isArray(this.events)) this.events = [];
        this.events.push(event);
        return this;
    }
    setEventsUrl(url, params = {}) {
        this.eventsUrl = url;
        this.eventsParams = params;
        return this;
    }
    setSources(sources, toggleUrl = null) {
        this.sources = sources;
        this.sourceToggleUrl = toggleUrl;
        return this;
    }
    addSource(id, label, color = null, active = true) {
        if (!Array.isArray(this.sources)) this.sources = [];
        this.sources.push({ id, label, color, active });
        return this;
    }
    setAddButton(url, label = null, modalTitle = null) {
        this.addUrl = url;
        this.addLabel = label;
        this.addModalTitle = modalTitle;
        return this;
    }
    setLinkTarget(target, modalWidth = '900px') {
        this.linkTarget = target;
        this.modalWidth = modalWidth;
        return this;
    }
    setHeight(height = 'auto', timeGridHeight = 720) {
        this.height = height;
        this.timeGridHeight = timeGridHeight;
        return this;
    }
    setTimeRange(slotMinTime = '00:00:00', slotMaxTime = '24:00:00', scrollTime = '07:00:00') {
        this.slotMinTime = slotMinTime;
        this.slotMaxTime = slotMaxTime;
        this.scrollTime = scrollTime;
        return this;
    }
    setInteraction(selectable = false, editable = false) {
        this.selectable = selectable;
        this.editable = editable;
        return this;
    }
    setDisplay(nowIndicator = true, weekNumbers = false, dayMaxEvents = true) {
        this.nowIndicator = nowIndicator;
        this.weekNumbers = weekNumbers;
        this.dayMaxEvents = dayMaxEvents;
        return this;
    }
    setLibUrl(v) { this.libUrl = v; return this; }

    async render() { return this._fetchRender('Calendar'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSCalendar, 'Calendar', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSAvatar — Avatar component (user photo + initials)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSAvatar extends DCMSFormElement {

    constructor() {
        super();
        this.name = null;
        this.src = null;
        this.size = null;            // 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
        this.shape = null;           // 'circle' | 'square'
        this.backgroundColor = null;
        this.showOnlineIndicator = null;
        this.isOnline = null;
        this.tooltip = null;
        this.href = null;
    }

    setName(v)            { this.name = v; return this; }
    setSrc(v)             { this.src = v; return this; }
    setSize(v)            { this.size = v; return this; }
    setShape(v)           { this.shape = v; return this; }
    setBackgroundColor(v) { this.backgroundColor = v; return this; }
    setOnlineIndicator(show = true, online = false) {
        this.showOnlineIndicator = show;
        this.isOnline = online;
        return this;
    }
    setTooltip(v)         { this.tooltip = v; return this; }
    setHref(v)            { this.href = v; return this; }

    async render() { return this._fetchRender('Avatar'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSAvatar, 'Avatar', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSSteps — Steps component (workflow visualization)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSSteps extends DCMSFormElement {

    constructor() {
        super();
        this.items = null;           // [{title, description?, icon?, status?}, ...]
        this.current = null;
        this.direction = null;       // 'horizontal' | 'vertical'
        this.size = null;            // 'default' | 'small'
        this.showNumbers = null;
        this.clickable = null;
        this.onClickUrl = null;
        this.onClickJs = null;
    }

    setItems(v)       { this.items = v; return this; }
    setCurrent(v)     { this.current = v; return this; }
    setDirection(v)   { this.direction = v; return this; }
    setSize(v)        { this.size = v; return this; }
    setShowNumbers(v) { this.showNumbers = v; return this; }
    setClickable(v, url = null, js = null) {
        this.clickable = v;
        this.onClickUrl = url;
        this.onClickJs = js;
        return this;
    }

    async render() { return this._fetchRender('Steps'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSSteps, 'Steps', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSTooltip — Tooltip component (hover hint)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSTooltip extends DCMSFormElement {

    constructor() {
        super();
        this.content = null;
        this.triggerId = null;
        this.position = null;        // 'top' | 'bottom' | 'left' | 'right' | ...
        this.trigger = null;         // 'hover' | 'click' | 'manual'
        this.showDelay = null;
        this.hideDelay = null;
        this.maxWidth = null;
        this.allowHtml = null;
    }

    setContent(v, allowHtml = false) {
        this.content = v;
        this.allowHtml = allowHtml;
        return this;
    }
    setTrigger(triggerId, trigger = 'hover') {
        this.triggerId = triggerId;
        this.trigger = trigger;
        return this;
    }
    setPosition(v)  { this.position = v; return this; }
    setDelays(show = 300, hide = 100) {
        this.showDelay = show;
        this.hideDelay = hide;
        return this;
    }
    setMaxWidth(v)  { this.maxWidth = v; return this; }

    async render() { return this._fetchRender('Tooltip'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSTooltip, 'Tooltip', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSPopconfirm — Popconfirm component (confirm popover)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSPopconfirm extends DCMSFormElement {

    constructor() {
        super();
        this.triggerId = null;
        this.title = null;
        this.message = null;
        this.okLabel = null;
        this.cancelLabel = null;
        this.okStyle = null;
        this.position = null;
        this.okUrl = null;
        this.onOkJs = null;
        this.onCancelJs = null;
        this.icon = null;
        this.informationStyle = null;
    }

    setTrigger(v)   { this.triggerId = v; return this; }
    setContent(message, title = null) {
        this.message = message;
        this.title = title;
        return this;
    }
    setButtons(okLabel = 'Tak', cancelLabel = 'Anuluj', okStyle = 'primary') {
        this.okLabel = okLabel;
        this.cancelLabel = cancelLabel;
        this.okStyle = okStyle;
        return this;
    }
    setOnOk(url = null, js = null) {
        this.okUrl = url;
        this.onOkJs = js;
        return this;
    }
    setOnCancel(js = null) { this.onCancelJs = js; return this; }
    setPosition(v) { this.position = v; return this; }
    setVariant(informationStyle = 'Warning', icon = 'help') {
        this.informationStyle = informationStyle;
        this.icon = icon;
        return this;
    }

    async render() { return this._fetchRender('Popconfirm'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSPopconfirm, 'Popconfirm', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSDrawer — Drawer component (slide-in panel)
// ──────────────────────────────────────────────────────────────────────────────

class DCMSDrawer extends DCMSFormElement {

    constructor() {
        super();
        this.drawerId = null;
        this.title = null;
        this.body = null;
        this.footer = null;
        this.placement = null;       // 'right' | 'left' | 'top' | 'bottom'
        this.size = null;            // 'sm' | 'md' | 'lg' | 'xl' | 'full'
        this.showOverlay = null;
        this.closable = null;
        this.closeOnOverlayClick = null;
        this.closeOnEsc = null;
        this.openOnInit = null;
        this.triggerId = null;
    }

    setDrawerId(v) { this.drawerId = v; return this; }
    setContent(body, title = null, footer = null) {
        this.body = body;
        this.title = title;
        this.footer = footer;
        return this;
    }
    setLayout(placement = 'right', size = 'md') {
        this.placement = placement;
        this.size = size;
        return this;
    }
    setCloseBehavior(closable = true, closeOnOverlayClick = true, closeOnEsc = true) {
        this.closable = closable;
        this.closeOnOverlayClick = closeOnOverlayClick;
        this.closeOnEsc = closeOnEsc;
        return this;
    }
    setOverlay(show = true)  { this.showOverlay = show; return this; }
    setTrigger(v)            { this.triggerId = v; return this; }
    setOpenOnInit(v = true)  { this.openOnInit = v; return this; }

    async render() { return this._fetchRender('Drawer'); }

    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSDrawer, 'Drawer', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSEditable — inline editing (equivalent to UI Editable / <Editable> React)
//
// Displays the value; activation swaps it for an actual editor (any form
// component), saved via buttons, auto-blur/Enter, or AJAX (saveUrl).
// Client-side behavior (popover/modal/inline, swap, save) is provided by the
// editable.head.tpl template — included automatically in the endpoint's response.
//
// The editor is passed as a DCMS* instance or a {component, params} object;
// it is built server-side (the same whitelist as FormLayout fields). Without an
// editor, the default text Input is used.
//
//   // instance as the editor
//   const el = await new DCMSEditable()
//       .setLabel('Status')
//       .setValue('2')
//       .setDisplayValue('W trakcie')
//       .setEditor(new DCMSSelect().setOptions([
//           { value: '1', label: 'Nowy' },
//           { value: '2', label: 'W trakcie' },
//           { value: '3', label: 'Zakończony' },
//       ]))
//       .render();
//   document.getElementById('target').appendChild(el);
//
//   // options object variant (mirrors EditableHelper::create())
//   const el2 = await DCMSEditable.create({
//       name: 'progress', label: 'Postęp', value: '40', displayValue: '40%',
//       editor: { component: 'Slider', params: { min: 0, max: 100, step: 5, color: 'emerald' } },
//   });
// ──────────────────────────────────────────────────────────────────────────────

class DCMSEditable extends DCMSFormElement {

    constructor() {
        super();
        this.editor = null;          // DCMS* instance | {component, params} | null (default: text Input)
        this.emptyText = null;       // placeholder for an empty value (default '—')
        this.displayValue = null;    // formatted HTML/text displayed instead of the raw value
        this.trigger = null;         // 'click' | 'icon' | 'both'
        this.saveMode = null;        // 'buttons' | 'auto'
        this.layout = null;          // 'popover' | 'modal' | 'inline'
        this.affordance = null;      // 'dashed' | 'dotted' | 'underline' | 'subtle' | 'none'
        this.color = null;           // Tailwind theme color

        // AJAX save (optional — equivalent to Editable::helperSetSave)
        this.saveUrl = null;
        this.recordId = null;
        this.saveMethod = null;      // 'PUT' | 'POST' | ...
        this.saveField = null;
        this.saveLabel = null;
        this.cancelLabel = null;
    }

    /** @param {DCMSFormElement|Object} editor Instancja DCMS* lub {component, params} */
    setEditor(editor)  { this.editor = editor; return this; }
    setEmptyText(v)    { this.emptyText = v; return this; }
    setDisplayValue(v) { this.displayValue = v; return this; }
    /** @param {string} v 'click' | 'icon' | 'both' */
    setTrigger(v)      { this.trigger = v; return this; }
    /** @param {string} v 'buttons' | 'auto' */
    setSaveMode(v)     { this.saveMode = v; return this; }
    /** @param {string} v 'popover' | 'modal' | 'inline' */
    setLayout(v)       { this.layout = v; return this; }
    /** @param {string} v 'dashed' | 'dotted' | 'underline' | 'subtle' | 'none' */
    setAffordance(v)   { this.affordance = v; return this; }
    setColor(v)        { this.color = v; return this; }

    setButtonLabels(saveLabel = 'Zapisz', cancelLabel = 'Anuluj') {
        this.saveLabel = saveLabel;
        this.cancelLabel = cancelLabel;
        return this;
    }

    /**
     * Configures AJAX save for a single value.
     * @param {string} url Save endpoint URL
     * @param {?string} recordId Record identifier sent in the payload
     * @param {string} method HTTP method (default 'PUT')
     */
    setSave(url, recordId = null, method = 'PUT') {
        this.saveUrl = url;
        this.recordId = recordId;
        this.saveMethod = method;
        return this;
    }
    setSaveField(v) { this.saveField = v; return this; }

    /**
     * Override: a DCMS* instance in `editor` → {component, params} spec for the endpoint.
     * A {component, params} object and null pass through unchanged.
     * @returns {Object}
     */
    _getParams() {
        const params = super._getParams();
        if (params.editor instanceof DCMSFormElement) {
            params.editor = DCMSFormLayout._toFieldConfig(params.editor);
        }
        return params;
    }

    async render() { return this._fetchRender('Editable'); }

    /**
     * @param {Object} options Options object — keys identical to EditableHelper::create() parameters
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        return DCMSFormElement._createFromOptions(DCMSEditable, 'Editable', options);
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// DCMSFormLayout — a complete form with a layout (equivalent to UI FormLayout)
//
// The form is arranged by a layout definition identical to the Dataset's editLayout():
// tabs → rows of a 12-column grid (width/rowspan) → panels (fieldset)
// → aside column. Fields are built server-side by the formBuilder endpoint
// (the same whitelist as individual components), and the whole form arrives
// in a single response — one fetch instead of a separate one for each field.
//
// Fluent builder (mirrors PHP $dataset->editLayout()):
//   const form = await new DCMSFormLayout()
//       .tab('Podstawowe')
//           .row()
//               .field('preview', new DCMSTextarea().setLabel('Podgląd').setRows(10), { width: 6, rowspan: 3 })
//               .field('type',    new DCMSSelect().setLabel('Typ').setOptions(types), { width: 6 })
//           .row().field('name',   new DCMSInput().setLabel('Nazwa').setRequired(true), { width: 6 })
//           .row().field('source', new DCMSInput().setLabel('Źródło'), { width: 6 })
//           .panel('Wycena', { id: 'panel-pricing' })
//               .row()
//                   .field('price',    new DCMSInput().setLabel('Cena').setType('number'), { width: 6 })
//                   .field('currency', new DCMSSelect().setLabel('Waluta').setOptions(currencies), { width: 6 })
//           .endPanel()
//       .setForm({ id: 'mediaForm', method: 'post', action: '#', submitButtonText: 'Zapisz' })
//       .render();
//
// Options object (1:1 equivalent of the endpoint):
//   const form = await DCMSFormLayout.create({
//       layout: { tabs: [{ title: 'Podstawowe', items: [
//           { type: 'row', columns: [{ id: 'name', width: 6 }, { id: 'source', width: 6 }] },
//       ]}]},
//       fields: {
//           name:   { component: 'Input', params: { label: 'Nazwa', required: true } },
//           source: new DCMSInput().setLabel('Źródło'),   // instances also work
//       },
//       form: { id: 'mediaForm', submitButtonText: 'Zapisz' },
//   });
// ──────────────────────────────────────────────────────────────────────────────

class DCMSFormLayout extends DCMSFormElement {

    constructor() {
        super();
        this.layout = { tabs: [] };
        this.fields = {};        // fieldId → {component: 'Input', params: {...}}
        this.form = null;        // FormHelper::create() parameters (id, method, submitButtonText, ...)
        this.withForm = null;    // false = layout only, without <form>
        this.tabsStyle = null;   // 'modern' | 'classic'
        this.gridGap = null;

        // Fluent builder cursors — skipped in _getParams()
        this._tab = null;
        this._row = null;
        this._panel = null;
    }

    /**
     * Starts a new tab
     * @param {?string} title Tab title
     * @param {?string} id Optional tab id
     */
    tab(title = null, id = null) {
        this._tab = { title, items: [] };
        if (id) this._tab.id = id;
        this.layout.tabs.push(this._tab);
        this._panel = null;
        this._row = null;
        return this;
    }

    /** Default tab, used when the caller starts with row()/panel() */
    _ensureTab() {
        if (!this._tab) this.tab(null);
        return this._tab;
    }

    /** Starts a new row (within the tab or an open panel) */
    row() {
        this._ensureTab();
        this._row = { columns: [] };
        if (this._panel) {
            this._panel.rows.push(this._row);
        } else {
            this._tab.items.push({ type: 'row', columns: this._row.columns });
        }
        return this;
    }

    /**
     * Adds a field to the current row
     * @param {string} id Field identifier (component name/id defaults to id)
     * @param {?(DCMSFormElement|Object)} component DCMS* instance or {component, params} (null = registered separately via addField)
     * @param {Object} layoutOpts { width: 1-12, rowspan: 1-12 }
     */
    field(id, component = null, layoutOpts = {}) {
        if (!this._row) this.row();
        const col = { id };
        if (layoutOpts.width) col.width = layoutOpts.width;
        if (layoutOpts.rowspan) col.rowspan = layoutOpts.rowspan;
        this._row.columns.push(col);
        if (component) this.addField(id, component);
        return this;
    }

    /**
     * Opens a panel (fieldset frame with a legend) in the current tab
     * @param {?string} title Panel title (legend)
     * @param {Object} opts { id, cssClass, headClass, bodyClass }
     */
    panel(title = null, opts = {}) {
        this._ensureTab();
        this._panel = { type: 'panel', title, rows: [], ...opts };
        this._tab.items.push(this._panel);
        this._row = null;
        return this;
    }

    /** Closes the open panel */
    endPanel() {
        this._panel = null;
        this._row = null;
        return this;
    }

    /**
     * Marks the last element (row/panel) for the aside column on wide fluid layouts
     * @param {?number} breakpoint Minimum width (px) that moves it to the 2nd column
     */
    aside(breakpoint = null) {
        const items = this._ensureTab().items;
        const last = items[items.length - 1];
        if (last) {
            last.aside = true;
            if (breakpoint) last.asideBreakpoint = breakpoint;
        }
        return this;
    }

    /**
     * Registers a field component
     * @param {string} id Field identifier from the layout
     * @param {DCMSFormElement|Object} component DCMS* instance or {component: 'Input', params: {...}}
     */
    addField(id, component) {
        this.fields[id] = DCMSFormLayout._toFieldConfig(component);
        return this;
    }

    /** Registers multiple fields at once: { fieldId: instance|config, ... } */
    setFields(map) {
        for (const [id, component] of Object.entries(map)) {
            this.addField(id, component);
        }
        return this;
    }

    /** FormHelper::create() parameters for the <form> wrapper */
    setForm(formOptions) { this.form = formOptions; return this; }

    setLayout(layout)   { this.layout = layout; return this; }
    setWithForm(v)      { this.withForm = v; return this; }
    setTabsStyle(v)     { this.tabsStyle = v; return this; }
    setGridGap(v)       { this.gridGap = v; return this; }

    /**
     * Normalizes a field component to the endpoint format {component, params}
     * @param {DCMSFormElement|Object} component
     */
    static _toFieldConfig(component) {
        if (component instanceof DCMSFormElement) {
            return {
                component: component.constructor.name.replace(/^DCMS/, ''),
                params: component._getParams(),
            };
        }
        if (component && typeof component === 'object' && component.component) {
            return component;
        }
        throw new Error('DCMSFormLayout: komponent pola musi być instancją DCMS* albo obiektem {component, params}');
    }

    /**
     * Override: sends only the endpoint properties (builder cursors stay local)
     * @returns {Object}
     */
    _getParams() {
        const params = { layout: this.layout, fields: this.fields };
        for (const key of ['form', 'withForm', 'tabsStyle', 'gridGap', 'id', 'class', 'style', 'dataAttr']) {
            if (this[key] !== null && this[key] !== undefined) {
                params[key] = this[key];
            }
        }
        return params;
    }

    async render() { return this._fetchRender('FormLayout'); }

    /**
     * @param {Object} options { layout, fields, form, withForm, tabsStyle, gridGap }
     * @returns {Promise<HTMLElement>}
     */
    static async create(options = {}) {
        const el = new DCMSFormLayout();
        if (options.layout) el.setLayout(options.layout);
        if (options.fields) el.setFields(options.fields);
        if (options.form) el.setForm(options.form);
        if (options.withForm !== undefined) el.setWithForm(options.withForm);
        if (options.tabsStyle) el.setTabsStyle(options.tabsStyle);
        if (options.gridGap) el.setGridGap(options.gridGap);
        return el.render();
    }
}

// ──────────────────────────────────────────────────────────────────────────────
// Export to window (global availability in <script> and onclick)
// ──────────────────────────────────────────────────────────────────────────────

window.DCMSFormElement = DCMSFormElement;
window.DCMSInput = DCMSInput;
window.DCMSTextarea = DCMSTextarea;
window.DCMSCheckbox = DCMSCheckbox;
window.DCMSSelect = DCMSSelect;
window.DCMSForm = DCMSForm;
window.DCMSRadio = DCMSRadio;
window.DCMSSwitchOnOff = DCMSSwitchOnOff;
window.DCMSDateTimePicker = DCMSDateTimePicker;
window.DCMSColorPicker = DCMSColorPicker;
window.DCMSSlider = DCMSSlider;
window.DCMSSortableList = DCMSSortableList;
window.DCMSTree = DCMSTree;
window.DCMSDualList = DCMSDualList;
window.DCMSButton = DCMSButton;
window.DCMSImage64 = DCMSImage64;
window.DCMSDataTables = DCMSDataTables;
window.DCMSTabs = DCMSTabs;
window.DCMSPanel = DCMSPanel;
window.DCMSGrid = DCMSGrid;
window.DCMSTable = DCMSTable;
window.DCMSAlert = DCMSAlert;
window.DCMSModal = DCMSModal;
window.DCMSSkeleton = DCMSSkeleton;
window.DCMSTag = DCMSTag;
window.DCMSTile = DCMSTile;
window.DCMSTileGrid = DCMSTileGrid;
window.DCMSCalendar = DCMSCalendar;
window.DCMSAvatar = DCMSAvatar;
window.DCMSSteps = DCMSSteps;
window.DCMSTooltip = DCMSTooltip;
window.DCMSPopconfirm = DCMSPopconfirm;
window.DCMSDrawer = DCMSDrawer;
window.DCMSEditable = DCMSEditable;
window.DCMSFormLayout = DCMSFormLayout;

export {
    DCMSFormElement, DCMSInput, DCMSTextarea, DCMSCheckbox, DCMSSelect, DCMSForm,
    DCMSRadio, DCMSSwitchOnOff, DCMSDateTimePicker, DCMSColorPicker, DCMSSlider,
    DCMSSortableList, DCMSTree, DCMSDualList, DCMSButton, DCMSImage64, DCMSDataTables,
    DCMSTabs, DCMSPanel, DCMSGrid, DCMSTable, DCMSAlert, DCMSModal,
    DCMSSkeleton, DCMSTag, DCMSTile, DCMSTileGrid, DCMSCalendar, DCMSAvatar, DCMSSteps, DCMSTooltip, DCMSPopconfirm, DCMSDrawer,
    DCMSEditable, DCMSFormLayout
};
